import React, { useEffect, useState } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from "@mui/material";

import { getProject, updateProject, getEmployeeCapacity } from "../../services/projectService";
import CapacitySlider from "../../ui/CapacitySlider";
import AppButton from "../../ui/AppButton";

function EditCapacityDialog({ open, employee, project, onClose, onSaved }) {
  const [capacityPercent, setCapacityPercent] = useState(0);
  const [availableCapacity, setAvailableCapacity] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !project) {
      return;
    }

    setCapacityPercent(project.capacity_percent || 0);
    setError("");

    getEmployeeCapacity(employee.employee_id, project.project_id, project.start_date, project.end_date)
      .then((capacity) => setAvailableCapacity(capacity.available_capacity_percent))
      .catch((err) => {
        console.error("Error loading employee capacity:", err);
        setAvailableCapacity(null);
      });
  }, [open, project, employee]);

  const handleSave = async () => {
    if (!project || !employee) {
      return;
    }

    try {
      setSaving(true);
      setError("");

      const fullProject = await getProject(project.project_id);

      const mergedAssignedTo = fullProject.assigned_to.map((a) =>
        a.employee_id === employee.employee_id ? { ...a, capacity_percent: capacityPercent } : a
      );

      await updateProject(project.project_id, {
        project_name: fullProject.project_name,
        description: fullProject.description,
        start_date: fullProject.start_date,
        end_date: fullProject.end_date,
        assigned_to: mergedAssignedTo
      });

      onSaved?.();
      onClose();
    } catch (err) {
      console.error("Error updating capacity:", err);
      setError(err?.response?.data?.detail || "Failed to update capacity");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Edit Capacity{project ? ` — ${project.project_name}` : ""}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <CapacitySlider
          name={employee ? employee.employee_name : "Employee"}
          value={capacityPercent}
          onChange={(e, value) => setCapacityPercent(value)}
          max={100}
        />

        {availableCapacity !== null && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Available capacity for this employee (excluding this project): {availableCapacity}%
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <AppButton variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </AppButton>

        <AppButton variant="contained" onClick={handleSave} loading={saving}>
          Save
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}

export default EditCapacityDialog;
